import { useMemo, useCallback } from "react";
import type {
  AppointmentBookingPage,
  CalendarEvent,
  ChatRoom,
  EnduserOrder,
  File,
  Form,
  FormResponse,
  ManagedContentRecord,
  Purchase,
} from "@tellescope/types-models";
import type { ItemFilter } from "../../shared/schema/blocks";
import type { VisibilityCondition, DataSourceType } from "@tellescope/visibility-schema";
import {
  normalizeStripeSubscription,
  normalizeStripeInvoice,
  type VisibilityContext,
} from "../../shared/visibility/types";
import type { StripeSubscription } from "./useStripeSubscriptions";
import type { StripeInvoice } from "./useStripeInvoices";
import { evaluateVisibility } from "../../shared/visibility/evaluator";
import { extractDataSources } from "../../shared/visibility/extractDataSources";
import { useVisibilityContext } from "./useVisibilityContext";

/**
 * Context builder function type for item filtering.
 * Takes the base context and the current item, returns extended context.
 */
type ContextBuilder<T> = (baseContext: VisibilityContext, item: T) => VisibilityContext;

/**
 * Generic item filter hook configuration.
 */
export interface UseItemFilterOptions<T> {
  /** Array of items to filter */
  items: T[];
  /** Optional item filter configuration from block props */
  itemFilter?: ItemFilter;
  /** Function to build context for each item (adds item-specific data to context) */
  buildContext: ContextBuilder<T>;
  /** The data source type for the item (excluded from external source fetching) */
  itemSourceType: DataSourceType;
}

/**
 * Result from useItemFilter hook.
 */
export interface UseItemFilterResult<T> {
  /** Filtered items that pass the condition */
  filteredItems: T[];
  /** Whether the filter data is still loading */
  isLoading: boolean;
}

/**
 * Extracts the data sources needed for item filtering, excluding the item's own source.
 * For example, when filtering forms, we exclude "form" since that comes from the item itself.
 *
 * @param condition - The filter condition
 * @param itemSource - The source type that comes from the item (e.g., "form")
 */
function extractExternalSources(condition: VisibilityCondition, itemSource: DataSourceType): Set<DataSourceType> {
  const allSources = extractDataSources(condition);
  allSources.delete(itemSource); // Item source comes from the item, not context
  return allSources;
}

/**
 * Generic hook for filtering items using visibility conditions.
 *
 * This hook enables client-side filtering of data block items (forms, appointments, etc.)
 * using the same condition system as block visibility. It:
 * 1. Extracts required data sources from the filter condition
 * 2. Fetches needed context data (enduser, formResponses, etc.)
 * 3. Evaluates the condition for each item
 * 4. Returns only items that pass the filter
 *
 * @example
 * ```tsx
 * const { filteredItems, isLoading } = useItemFilter({
 *   items: forms,
 *   itemFilter: block.props.itemFilter,
 *   itemSourceType: "form",
 *   buildContext: (baseContext, formDisplay) => ({
 *     ...baseContext,
 *     form: formDisplay.form,
 *   }),
 * });
 * ```
 */
export function useItemFilter<T>(options: UseItemFilterOptions<T>): UseItemFilterResult<T> {
  const { items, itemFilter, buildContext, itemSourceType } = options;

  // Extract required data sources from the filter condition
  const requiredSources = useMemo(() => {
    if (!itemFilter) return new Set<DataSourceType>();
    return extractExternalSources(itemFilter.condition, itemSourceType);
  }, [itemFilter, itemSourceType]);

  // Fetch required context data
  const { context: baseContext, isLoading } = useVisibilityContext({ requiredSources });

  // Filter items using the condition
  const filteredItems = useMemo(() => {
    // If no filter, return all items
    if (!itemFilter) return items;

    // If still loading context, return empty (will show loading state)
    if (isLoading || !baseContext) return [];

    // Evaluate condition for each item
    return items.filter((item) => {
      const itemContext = buildContext(baseContext, item);
      return evaluateVisibility(itemFilter.condition, itemContext);
    });
  }, [items, itemFilter, isLoading, baseContext, buildContext]);

  return {
    filteredItems,
    isLoading: itemFilter ? isLoading : false,
  };
}

// ============================================
// Specialized filter hooks for each data type
// ============================================

/**
 * Filter hook for Pinned Forms (Form objects).
 */
export function usePinnedFormsFilter<T extends { form: Form }>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      form: item.form,
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "form",
    buildContext,
  });
}

/**
 * Filter hook for Appointments (CalendarEvent objects).
 */
export function useAppointmentsFilter<T extends CalendarEvent>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      appointment: item,
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "appointment",
    buildContext,
  });
}

/**
 * Filter hook for Orders (EnduserOrder objects).
 */
export function useOrdersFilter<T extends EnduserOrder>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      order: item,
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "order",
    buildContext,
  });
}

/**
 * Filter hook for Chat Rooms (ChatRoom objects).
 */
export function useChatRoomsFilter<T extends ChatRoom>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      chatRoom: item,
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "chatRoom",
    buildContext,
  });
}

/**
 * Filter hook for Files (File objects).
 */
export function useFilesFilter<T extends File>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      file: item,
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "file",
    buildContext,
  });
}

/**
 * Filter hook for Form Responses (FormResponse objects).
 * Note: This is for filtering within the form-response-list block,
 * not for the formResponse visibility source.
 */
export function useFormResponsesFilter<T extends FormResponse>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      // Use formResponse source for the current item being evaluated
      // This overwrites the formResponses array context with a single-item evaluation
      formResponses: [item],
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "formResponse",
    buildContext,
  });
}

/**
 * Filter hook for Content (ManagedContentRecord objects).
 */
export function useContentFilter<T extends ManagedContentRecord>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      content: item,
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "content",
    buildContext,
  });
}

/**
 * Filter hook for Purchases (Purchase objects).
 */
export function usePurchasesFilter<T extends Purchase>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      purchase: item,
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "purchase",
    buildContext,
  });
}

/**
 * Filter hook for Stripe Subscriptions (StripeSubscription objects).
 * Normalizes raw subscription data for condition evaluation.
 */
export function useStripeSubscriptionsFilter<T extends StripeSubscription>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      stripeSubscription: normalizeStripeSubscription(item),
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "stripeSubscription",
    buildContext,
  });
}

/**
 * Filter hook for Stripe Invoices (StripeInvoice objects).
 * Normalizes raw invoice data for condition evaluation.
 */
export function useStripeInvoicesFilter<T extends StripeInvoice>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      stripeInvoice: normalizeStripeInvoice(item),
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "stripeInvoice",
    buildContext,
  });
}

/**
 * Filter hook for Appointment Booking Pages (AppointmentBookingPage objects).
 */
export function useAppointmentBookingPagesFilter<T extends AppointmentBookingPage>(options: {
  items: T[];
  itemFilter?: ItemFilter;
}): UseItemFilterResult<T> {
  const buildContext = useCallback(
    (baseContext: VisibilityContext, item: T): VisibilityContext => ({
      ...baseContext,
      appointmentBookingPage: item,
    }),
    []
  );

  return useItemFilter({
    items: options.items,
    itemFilter: options.itemFilter,
    itemSourceType: "appointmentBookingPage",
    buildContext,
  });
}
